import BaseTooltip from 'components/Tooltips/BaseTooltip';
import ResponderBaseTable from 'features/Responder/components/ResponderBaseTable';
import ResponderDropdownSelector from 'features/Responder/components/ResponderDropdownSelector';
import { useTours } from 'features/Responder/features/Tours';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MdSecurity as FaSecurity } from 'react-icons/md';
import { useGetDutiesQuery } from 'services/duties/duties.api';
import {
  extractPersonnelIsSupervisor,
  stringifyId,
  timeAgo,
  toStringDutyIds,
} from '../utils/personnelListUtils';
import './Table.css';

const Table = ({
  personnel,
  onSelectPersonnel = null,
  personnelDutyIdByUserId = {},
  onSelectPersonnelDuty = async () => true,
}) => {
  const { setCenter } = useTours();
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [pendingDutyByUserId, setPendingDutyByUserId] = useState({});
  const [pendingSupervisorByUserId, setPendingSupervisorByUserId] = useState({});
  const [openDutyDropdownUserId, setOpenDutyDropdownUserId] = useState(null);
  const { data: duties = [] } = useGetDutiesQuery();

  const dutyOptions = useMemo(
    () =>
      duties.map((duty) => ({
        id: stringifyId(duty?.id),
        label: duty?.name || `Duty ${duty?.id}`,
      })),
    [duties],
  );

  useEffect(() => {
    setPendingDutyByUserId({});
  }, [personnelDutyIdByUserId]);

  useEffect(() => {
    setPendingSupervisorByUserId((prev) => {
      const next = { ...prev };
      let didChange = false;

      Object.keys(prev).forEach((userKey) => {
        const person = personnel?.[userKey];
        if (!person) {
          delete next[userKey];
          didChange = true;
          return;
        }

        const hasSupervisorValue =
          person?.isSupervisor != null ||
          person?.is_supervisor != null ||
          person?.isSupervisorDuty != null;

        if (!hasSupervisorValue) return;

        const serverSupervisorValue = extractPersonnelIsSupervisor(person);
        if (serverSupervisorValue === prev[userKey]) {
          delete next[userKey];
          didChange = true;
        }
      });

      return didChange ? next : prev;
    });
  }, [personnel]);

  const handleRowClick = (person) => {
    setOpenDutyDropdownUserId(null);
    if (typeof onSelectPersonnel === 'function') {
      onSelectPersonnel(person);
      return;
    }
    const loc = person.location;
    if (loc?.lat != null && loc?.lng != null) {
      const targetZoom = 16;
      setCenter([loc.lat, loc.lng, targetZoom]);
    }
  };

  const rows = useMemo(
    () =>
      Object.values(personnel).map((person) => ({
        id: person.userId,
        userId: person.userId,
        name: person.name,
        clockedIn: person.clockedIn,
        clockInTime: person.clockInTime,
        clockOutTime: person.clockOutTime,
        location: person.location,
        hasSupervisorValue:
          person?.isSupervisor != null ||
          person?.is_supervisor != null ||
          person?.isSupervisorDuty != null,
        isSupervisor: extractPersonnelIsSupervisor(person),
      })),
    [personnel],
  );

  const handleDutyChange = useCallback(
    async (userId, nextDutyIds, isSupervisor) => {
      const userKey = stringifyId(userId);
      const previousDutyValues = toStringDutyIds(
        pendingDutyByUserId[userKey] ?? personnelDutyIdByUserId[userKey],
      );
      const nextDutyValues = toStringDutyIds(nextDutyIds);

      setPendingDutyByUserId((prev) => ({
        ...prev,
        [userKey]: nextDutyValues,
      }));

      const didSave = await onSelectPersonnelDuty(userId, nextDutyValues, Boolean(isSupervisor));
      if (didSave === false) {
        setPendingDutyByUserId((prev) => ({
          ...prev,
          [userKey]: previousDutyValues,
        }));
      }
    },
    [onSelectPersonnelDuty, pendingDutyByUserId, personnelDutyIdByUserId],
  );

  const handleSupervisorChange = useCallback(
    async (userId, nextChecked, currentDutyIds, previousChecked) => {
      const userKey = stringifyId(userId);
      const nextSupervisorValue = Boolean(nextChecked);
      const previousSupervisorValue = Boolean(previousChecked);

      setPendingSupervisorByUserId((prev) => ({
        ...prev,
        [userKey]: nextSupervisorValue,
      }));

      const didSave = await onSelectPersonnelDuty(userId, currentDutyIds, nextSupervisorValue);
      if (didSave === false) {
        setPendingSupervisorByUserId((prev) => ({
          ...prev,
          [userKey]: previousSupervisorValue,
        }));
      }
    },
    [onSelectPersonnelDuty],
  );

  const columns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        size: 260,
        Cell: ({ row, cell }) => (
          <div className="d-flex align-items-center gap-2">
            <p
              className="text-theme fs-6 text-truncate mb-0"
              style={{ maxWidth: 'calc(100% - 20px)' }}
            >
              {cell.getValue()}
            </p>
            {row.original.isSupervisor ? (
                <BaseTooltip id={`personnel-supervisor-${row.original.userId}`} text="Supervisor">
                  <span className="d-inline-flex" aria-label="Supervisor">
                    <FaSecurity size={14} color="var(--color-status-success)" />
                  </span>
                </BaseTooltip>
              ) : null}
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'clockedIn',
        size: 120,
        Cell: ({ cell }) => {
          const isOnline = cell.getValue();
          return (
            <p className={`badge ${isOnline ? 'bg-success' : 'bg-secondary'} text-center`}>
              {isOnline ? 'Clocked In' : 'Offline'}
            </p>
          );
        },
      },
      {
        header: 'Since',
        accessorKey: 'clockInTime',
        size: 140,
        Cell: ({ row, cell }) => {
          const person = row.original;
          let timestamp;

          if (!person.clockedIn && person.clockOutTime) {
            timestamp = person.clockOutTime;
          } else if (person.location?.timestamp) {
            timestamp = person.location.timestamp;
          } else {
            timestamp = cell.getValue();
          }

          return (
            <div className="text-theme text-nowrap fs-6">{timeAgo(timestamp, currentTime)}</div>
          );
        },
      },
      {
        header: 'Duties',
        id: 'duties',
        size: 260,
        enableSorting: false,
        Cell: ({ row }) => {
          const userId = row.original.userId;
          const userKey = stringifyId(userId);
          const selectedDutyIds = toStringDutyIds(
            pendingDutyByUserId[userKey] ?? personnelDutyIdByUserId[userKey],
          );

          const baseOptions = [...dutyOptions];
          const optionById = new Map(baseOptions.map((option) => [option.id, option]));

          const prioritizedSelectedOptions = selectedDutyIds.map((dutyId) => {
            const existing = optionById.get(dutyId);
            if (existing) return existing;
            return {
              id: dutyId,
              label: `Duty ${dutyId} (unavailable)`,
            };
          });

          const selectedLookup = new Set(selectedDutyIds);
          const remainingOptions = baseOptions.filter((option) => !selectedLookup.has(option.id));

          const cellOptions = [...prioritizedSelectedOptions, ...remainingOptions];

          const selectedOptionIndexes = selectedDutyIds
            .map((dutyId) => cellOptions.findIndex((option) => option.id === dutyId))
            .filter((index) => index >= 0);
          const isDutyDropdownOpen = openDutyDropdownUserId === userKey;
          const supervisorChecked = Boolean(
            pendingSupervisorByUserId[userKey] ??
              (row.original.hasSupervisorValue ? row.original.isSupervisor : false),
          );

          return (
            <div
              className={`personnel-duty-dropdown${isDutyDropdownOpen ? ' is-open' : ''}`}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <ResponderDropdownSelector
                options={cellOptions.map((option) => option.label)}
                selectedOption={selectedOptionIndexes}
                show={isDutyDropdownOpen}
                onToggle={(nextShow) => {
                  setOpenDutyDropdownUserId(nextShow ? userKey : null);
                }}
                setSelected={(nextIndexes) => {
                  const nextDutyIds = nextIndexes
                    .map((index) => cellOptions[index]?.id)
                    .filter(Boolean);
                  handleDutyChange(userId, nextDutyIds, supervisorChecked);
                }}
                multiple
                haveCheckboxes
                reorderable
                showPinnedTopCheckbox
                pinnedTopCheckboxLabel="Supervisor"
                pinnedTopCheckboxChecked={supervisorChecked}
                onPinnedTopCheckboxChange={(nextChecked) => {
                  handleSupervisorChange(
                    userId,
                    nextChecked,
                    selectedDutyIds,
                    supervisorChecked,
                  );
                }}
                dropDirection="down"
                onChange={(event) => event?.stopPropagation?.()}
                contentStyle={{
                  maxHeight: 320,
                  overflowY: 'auto',
                }}
              />
            </div>
          );
        },
      },
    ],
    [
      currentTime,
      dutyOptions,
      handleDutyChange,
      openDutyDropdownUserId,
      pendingDutyByUserId,
      pendingSupervisorByUserId,
      personnelDutyIdByUserId,
      handleSupervisorChange,
    ],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  return (
    <ResponderBaseTable
      rows={rows}
      columns={columns}
      onRowClick={handleRowClick}
      enableRowVirtualization={false}
      muiTableContainerProps={{
        sx: {
          overflowX: 'visible',
          overflowY: 'visible',
        },
      }}
      muiTablePaperProps={{
        sx: {
          overflow: 'visible',
        },
      }}
      customStyles={{
        cell: {
          borderBottom: '1px solid var(--color-divider)',
          cursor: 'pointer',
          color: 'var(--color-font)',
          position: 'relative',
          overflow: 'visible',
        },
        row: {
          position: 'relative',
          overflow: 'visible',
          '&:hover': {
            backgroundColor: 'color-mix(in srgb, var(--color-primary) 56%, transparent)',
          },
        },
      }}
    />
  );
};

export default Table;
